import { api } from "@/api";
import { MutationConfig, MutationResult } from "@/shared/types/base";
import { sleep } from "@/shared/utils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ReactNode } from "react";

interface CancelPagoResponse {
  success: boolean;
  message: string;
  data: {
    pagoId: string;
    ventaId: string;
    mensaje: string;
    detalles: {
      ventaAnulada: boolean;
      movimientoCajaRevertido: boolean;
    };
  };
}

export async function cancelPago(ventaId: string): Promise<CancelPagoResponse> {
  await sleep(1000);
  const response = await api.put<CancelPagoResponse>(`caja_actual/anular-pago/${ventaId}`);
  return response.data;
}

export function useCancelPagoMutation(ventaId: string, mutationConfig: MutationConfig<CancelPagoResponse> = {}) {
  const queryClient = useQueryClient();
  return useMutation({
    ...mutationConfig,
    mutationFn: async () => await cancelPago(ventaId),
    onSuccess: (...args) => {
      mutationConfig.onSuccess && mutationConfig.onSuccess(...args);
      queryClient.invalidateQueries({ queryKey: ["ventas"] });
      queryClient.invalidateQueries({ queryKey: ["caja_actual"] });
    },
  })
}

export interface CancelPagoMutationProps {
  ventaId: string,
  mutationConfig?: MutationConfig<CancelPagoResponse>,
  children?: (mutationResult: MutationResult<CancelPagoResponse>) => ReactNode
}

export function CancelPagoMutation({
  ventaId,
  children,
  mutationConfig
}: CancelPagoMutationProps) {
  const mutationResult = useCancelPagoMutation(ventaId, mutationConfig);
  return children && children(mutationResult)
}
